import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCompanyBySlug, updateCompany, toggleCompanyStatus } from '../../lib/supabase/companies';
import {
    getAllUsers,
    getPendingInvitations,
    inviteUser,
    cancelInvitation,
    resendInvitation,
    deleteUser
} from '../../lib/supabase/users';
import InviteUserModal from '../common/InviteUserModal';
import { supabase } from '../../config/supabase';
import {
    FiArrowLeft, FiEdit2, FiSave, FiX, FiHome, FiMail, FiPhone, FiMapPin,
    FiGlobe, FiDollarSign, FiUsers, FiSettings, FiCheckCircle, FiAlertCircle,
    FiClock, FiCalendar, FiTrendingUp, FiActivity, FiPlus, FiTrash2, FiRefreshCw,
    FiUpload, FiImage
} from 'react-icons/fi';

// Move components outside to prevent re-creation on each render
const EditableSection = ({ title, icon: Icon, section, editingSection, saving, onStartEdit, onCancel, onSave, children }) => {
    const isEditing = editingSection === section;

    return (
        <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-6">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                    <Icon className="w-5 h-5 text-blue-600" />
                    {title}
                </h3>
                <div className="flex items-center gap-2">
                    {isEditing ? (
                        <>
                            <button
                                onClick={onSave}
                                disabled={saving}
                                className="flex items-center gap-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                            >
                                <FiSave className="w-4 h-4" />
                                {saving ? 'Saving...' : 'Save'}
                            </button>
                            <button
                                onClick={onCancel}
                                disabled={saving}
                                className="flex items-center gap-1 px-3 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                <FiX className="w-4 h-4" />
                                Cancel
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onStartEdit}
                            className="flex items-center gap-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                        >
                            <FiEdit2 className="w-4 h-4" />
                            Edit
                        </button>
                    )}
                </div>
            </div>
            {children}
        </div>
    );
};

const InfoRow = ({ label, value, field, type = 'text', options, editData, setEditData, isEditing }) => {
    return (
        <div className="flex justify-between items-center py-3 border-b border-gray-100 dark:border-zinc-700 last:border-0">
            <span className="text-sm text-gray-600 dark:text-gray-400 font-medium">{label}</span>
            {isEditing ? (
                type === 'select' ? (
                    <select
                        value={editData[field] || ''}
                        onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                        {options.map(opt => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                ) : type === 'color' ? (
                    <input
                        type="color"
                        value={editData[field] || '#3b82f6'}
                        onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                        className="w-20 h-10 border border-gray-300 dark:border-zinc-600 rounded-lg cursor-pointer"
                    />
                ) : type === 'number' ? (
                    <input
                        type="number"
                        value={editData[field] || ''}
                        onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-40"
                        step="0.01"
                    />
                ) : (
                    <input
                        type={type}
                        value={editData[field] || ''}
                        onChange={(e) => setEditData({ ...editData, [field]: e.target.value })}
                        className="px-3 py-2 border border-gray-300 dark:border-zinc-600 dark:bg-zinc-700 dark:text-white rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
                    />
                )
            ) : (
                <span className="font-medium text-gray-900 dark:text-white text-sm">
                    {value || 'Not set'}
                </span>
            )}
        </div>
    );
};

const CompanyDetails = () => {
    const navigate = useNavigate();
    const { companySlug: currentCompanySlug, targetCompanySlug } = useParams();
    const { userProfile } = useAuth();
    const [company, setCompany] = useState(null);
    const [editingSection, setEditingSection] = useState(null);
    const [editData, setEditData] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    // Logo upload state
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [uploadingLogo, setUploadingLogo] = useState(false);

    // Users state
    const [users, setUsers] = useState([]);
    const [pendingInvitations, setPendingInvitations] = useState([]);
    const [showInviteModal, setShowInviteModal] = useState(false);

    useEffect(() => {
        fetchCompanyData();
    }, [targetCompanySlug]);

    const fetchCompanyData = async () => {
        try {
            setLoading(true);
            const companyData = await getCompanyBySlug(targetCompanySlug);
            setCompany(companyData);

            // Fetch users for this company
            const usersData = await getAllUsers(companyData.id);
            const allUsers = [
                ...usersData.admins,
                ...usersData.designers,
                ...usersData.estimators,
                ...usersData.drivers
            ];
            setUsers(allUsers);

            // Fetch pending invitations
            const invitationsData = await getPendingInvitations(companyData.id);
            setPendingInvitations(invitationsData);
        } catch (error) {
            console.error('Error fetching company:', error);
            alert('Failed to load company details');
        } finally {
            setLoading(false);
        }
    };

    const startEditing = (section, currentData) => {
        setEditingSection(section);
        setEditData(currentData);
    };

    const cancelEditing = () => {
        setEditingSection(null);
        setEditData({});
        setLogoFile(null);
        setLogoPreview(null);
    };

    const handleLogoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Validate file type
        if (!file.type.startsWith('image/')) {
            alert('Please upload an image file');
            return;
        }

        // Validate file size (max 2MB)
        if (file.size > 2 * 1024 * 1024) {
            alert('Image size should be less than 2MB');
            return;
        }

        setLogoFile(file);

        // Create preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const uploadLogoToStorage = async () => {
        if (!logoFile) return null;

        try {
            setUploadingLogo(true);

            // Generate unique filename
            const fileExt = logoFile.name.split('.').pop();
            const fileName = `${company.slug || Date.now()}-logo.${fileExt}`;
            const filePath = `company-logos/${fileName}`;

            // Upload to Supabase storage
            const { data, error } = await supabase.storage
                .from('public-images')
                .upload(filePath, logoFile, {
                    cacheControl: '3600',
                    upsert: true
                });

            if (error) throw error;

            // Get public URL
            const { data: { publicUrl } } = supabase.storage
                .from('public-images')
                .getPublicUrl(filePath);

            return publicUrl;
        } catch (error) {
            console.error('Error uploading logo:', error);
            throw error;
        } finally {
            setUploadingLogo(false);
        }
    };

    const saveEdit = async () => {
        try {
            setSaving(true);

            // Upload logo if a new file is selected
            let updatedData = { ...company, ...editData };
            if (logoFile) {
                const logoUrl = await uploadLogoToStorage();
                updatedData.logo_url = logoUrl;
            }

            await updateCompany(company.id, updatedData);
            setCompany(updatedData);
            setEditingSection(null);
            setEditData({});
            setLogoFile(null);
            setLogoPreview(null);
            alert('Company updated successfully!');
        } catch (error) {
            console.error('Error updating company:', error);
            alert('Failed to update company: ' + error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleToggleStatus = async () => {
        try {
            const newStatus = !company.is_active;
            await toggleCompanyStatus(company.id, newStatus);
            setCompany({ ...company, is_active: newStatus });
            alert(`Company ${newStatus ? 'activated' : 'deactivated'} successfully!`);
        } catch (error) {
            console.error('Error toggling company status:', error);
            alert('Failed to update company status');
        }
    };

    const handleInviteUser = async (formData) => {
        if (!formData.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
            alert('Please enter a valid email address');
            return;
        }

        try {
            const result = await inviteUser(
                formData,
                company.id,
                company.name,
                userProfile.id,
                userProfile.username || userProfile.full_name
            );

            alert(result.message + '\n\nInvitation URL: ' + result.inviteUrl);
            setShowInviteModal(false);
            fetchCompanyData();
        } catch (error) {
            console.error('Error inviting user:', error);
            alert('Failed to send invitation: ' + error.message);
        }
    };

    const handleCancelInvitation = async (invitationId) => {
        if (!window.confirm('Are you sure you want to cancel this invitation?')) return;

        try {
            await cancelInvitation(invitationId);
            alert('Invitation cancelled successfully');
            fetchCompanyData();
        } catch (error) {
            console.error('Error cancelling invitation:', error);
            alert('Failed to cancel invitation');
        }
    };

    const handleResendInvitation = async (invitationId) => {
        try {
            const result = await resendInvitation(invitationId);
            alert(result.message);
            fetchCompanyData();
        } catch (error) {
            console.error('Error resending invitation:', error);
            alert('Failed to resend invitation');
        }
    };

    const handleDeleteUser = async (userId, username) => {
        if (!window.confirm(`Are you sure you want to delete ${username}?`)) return;

        try {
            await deleteUser(userId);
            alert('User deleted successfully');
            fetchCompanyData();
        } catch (error) {
            console.error('Error deleting user:', error);
            alert('Failed to delete user');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'Not set';
        return new Date(dateString).toLocaleDateString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
        );
    }

    if (!company) {
        return (
            <div className="text-center py-16">
                <FiAlertCircle className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Company not found</h3>
                <button
                    onClick={() => navigate(`/${currentCompanySlug}/companies`)}
                    className="text-blue-600 hover:text-blue-700 font-medium"
                >
                    Back to Companies
                </button>
            </div>
        );
    }

    return (
        <div className="mobile-management-page company-detail-page space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <button
                    onClick={() => navigate(`/${currentCompanySlug}/companies`)}
                    className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white transition-colors"
                >
                    <FiArrowLeft className="w-5 h-5" />
                    Back to Companies
                </button>

                <button
                    onClick={handleToggleStatus}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
                        company.is_active
                            ? 'bg-red-100 dark:bg-red-900/20 text-red-700 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/30'
                            : 'bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-300 hover:bg-green-200 dark:hover:bg-green-900/30'
                    }`}
                >
                    {company.is_active ? (
                        <>
                            <FiAlertCircle className="w-4 h-4" />
                            Deactivate Company
                        </>
                    ) : (
                        <>
                            <FiCheckCircle className="w-4 h-4" />
                            Activate Company
                        </>
                    )}
                </button>
            </div>

            {/* Company Header Card */}
            <div
                className="relative overflow-hidden rounded-2xl p-8 text-white"
                style={{
                    background: `linear-gradient(135deg, ${company.primary_color || '#3b82f6'} 0%, ${company.secondary_color || '#8b5cf6'} 100%)`
                }}
            >
                <div className="absolute inset-0 bg-black/10"></div>
                <div className="relative z-10">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-6">
                            {company.logo_url ? (
                                <img
                                    src={company.logo_url}
                                    alt={company.name}
                                    className="w-20 h-20 rounded-xl border-4 border-white bg-white object-cover shadow-lg"
                                />
                            ) : (
                                <div className="w-20 h-20 rounded-xl border-4 border-white bg-white flex items-center justify-center shadow-lg">
                                    <FiHome className="w-10 h-10 text-gray-400" />
                                </div>
                            )}
                            <div>
                                <h1 className="text-4xl font-bold mb-2">{company.name}</h1>
                                <p className="text-white/80 font-mono text-lg">/{company.slug}</p>
                                <div className="flex items-center gap-3 mt-3">
                                    {company.is_active ? (
                                        <div className="flex items-center gap-1 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                                            <FiCheckCircle className="w-4 h-4" />
                                            <span className="text-sm font-medium">Active</span>
                                        </div>
                                    ) : (
                                        <div className="flex items-center gap-1 px-3 py-1 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                                            <FiAlertCircle className="w-4 h-4" />
                                            <span className="text-sm font-medium">Inactive</span>
                                        </div>
                                    )}
                                    <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                                        <span className="text-sm font-medium capitalize">{company.subscription_plan} Plan</span>
                                    </div>
                                    <div className="px-3 py-1 bg-white/20 backdrop-blur-sm rounded-lg border border-white/30">
                                        <span className="text-sm font-medium capitalize">{company.subscription_status}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-xl p-6 border border-blue-200 dark:border-blue-800">
                    <FiUsers className="w-8 h-8 text-blue-600 dark:text-blue-400 mb-3" />
                    <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">{users.length}</p>
                    <p className="text-sm text-blue-600 dark:text-blue-400 font-medium">Total Users</p>
                </div>

                <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-6 border border-green-200 dark:border-green-800">
                    <FiDollarSign className="w-8 h-8 text-green-600 dark:text-green-400 mb-3" />
                    <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                        {company.currency} ${company.base_price}
                    </p>
                    <p className="text-sm text-green-600 dark:text-green-400 font-medium">Base Price</p>
                </div>

                <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-xl p-6 border border-purple-200 dark:border-purple-800">
                    <FiClock className="w-8 h-8 text-purple-600 dark:text-purple-400 mb-3" />
                    <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">{pendingInvitations.length}</p>
                    <p className="text-sm text-purple-600 dark:text-purple-400 font-medium">Pending Invites</p>
                </div>

                <div className="bg-gradient-to-br from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20 rounded-xl p-6 border border-amber-200 dark:border-amber-800">
                    <FiCalendar className="w-8 h-8 text-amber-600 dark:text-amber-400 mb-3" />
                    <p className="text-2xl font-bold text-amber-900 dark:text-amber-100">{formatDate(company.created_at)}</p>
                    <p className="text-sm text-amber-600 dark:text-amber-400 font-medium">Created</p>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Basic Information */}
                <EditableSection
                    title="Basic Information"
                    icon={FiHome}
                    section="basic"
                    editingSection={editingSection}
                    saving={saving}
                    onStartEdit={() => startEditing('basic', company)}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                >
                    <InfoRow label="Company Name" value={company.name} field="name" editData={editData} setEditData={setEditData} isEditing={editingSection === 'basic'} />
                    <InfoRow label="Slug" value={company.slug} field="slug" editData={editData} setEditData={setEditData} isEditing={editingSection === 'basic'} />

                    {/* Logo Upload Section */}
                    <div className="py-3 border-b border-gray-100 dark:border-zinc-700 last:border-0">
                        <span className="text-sm text-gray-600 dark:text-gray-400 font-medium block mb-3">Company Logo</span>
                        {editingSection === 'basic' ? (
                            <div className="space-y-3">
                                <div className="flex items-start gap-4">
                                    <div className="flex-1">
                                        <label className="flex flex-col items-center justify-center w-full h-32 px-4 border-2 border-dashed border-gray-300 dark:border-zinc-600 rounded-lg cursor-pointer hover:border-blue-500 dark:hover:border-blue-400 transition-colors bg-gray-50 dark:bg-zinc-700/50">
                                            <FiUpload className="w-8 h-8 mb-2 text-gray-400" />
                                            <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                                                <span className="font-medium text-blue-600 dark:text-blue-400">Click to upload</span> or drag and drop
                                            </p>
                                            <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                                                PNG, JPG, SVG (MAX. 2MB)
                                            </p>
                                            <input
                                                type="file"
                                                className="hidden"
                                                accept="image/*"
                                                onChange={handleLogoUpload}
                                            />
                                        </label>
                                    </div>

                                    {/* Preview */}
                                    {(logoPreview || company.logo_url) && (
                                        <div className="flex-shrink-0">
                                            <div className="w-32 h-32 border-2 border-gray-300 dark:border-zinc-600 rounded-lg overflow-hidden bg-white dark:bg-zinc-800">
                                                <img
                                                    src={logoPreview || company.logo_url}
                                                    alt="Logo preview"
                                                    className="w-full h-full object-contain"
                                                />
                                            </div>
                                            {logoPreview && (
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setLogoFile(null);
                                                        setLogoPreview(null);
                                                    }}
                                                    className="mt-2 w-full text-xs text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {uploadingLogo && (
                                    <p className="text-xs text-blue-600 dark:text-blue-400">Uploading logo...</p>
                                )}
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                {company.logo_url ? (
                                    <>
                                        <img
                                            src={company.logo_url}
                                            alt="Company logo"
                                            className="w-16 h-16 object-contain border border-gray-200 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 p-1"
                                        />
                                        <div className="flex-1">
                                            <p className="text-sm text-gray-900 dark:text-white font-medium">Logo uploaded</p>
                                            <a
                                                href={company.logo_url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-xs text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
                                            >
                                                View full size
                                            </a>
                                        </div>
                                    </>
                                ) : (
                                    <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                                        <FiImage className="w-5 h-5" />
                                        <span className="text-sm">No logo uploaded</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <InfoRow label="Primary Color" value={company.primary_color} field="primary_color" type="color" editData={editData} setEditData={setEditData} isEditing={editingSection === 'basic'} />
                    <InfoRow label="Secondary Color" value={company.secondary_color} field="secondary_color" type="color" editData={editData} setEditData={setEditData} isEditing={editingSection === 'basic'} />
                </EditableSection>

                {/* Contact Information */}
                <EditableSection
                    title="Contact Information"
                    icon={FiMail}
                    section="contact"
                    editingSection={editingSection}
                    saving={saving}
                    onStartEdit={() => startEditing('contact', company)}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                >
                    <InfoRow label="Contact Email" value={company.contact_email} field="contact_email" type="email" editData={editData} setEditData={setEditData} isEditing={editingSection === 'contact'} />
                    <InfoRow label="Contact Phone" value={company.contact_phone} field="contact_phone" type="tel" editData={editData} setEditData={setEditData} isEditing={editingSection === 'contact'} />
                    <InfoRow label="Billing Email" value={company.billing_email} field="billing_email" type="email" editData={editData} setEditData={setEditData} isEditing={editingSection === 'contact'} />
                </EditableSection>

                {/* Address */}
                <EditableSection
                    title="Address"
                    icon={FiMapPin}
                    section="address"
                    editingSection={editingSection}
                    saving={saving}
                    onStartEdit={() => startEditing('address', company)}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                >
                    <InfoRow label="Street Address" value={company.address} field="address" editData={editData} setEditData={setEditData} isEditing={editingSection === 'address'} />
                    <InfoRow label="City" value={company.city} field="city" editData={editData} setEditData={setEditData} isEditing={editingSection === 'address'} />
                    <InfoRow label="State" value={company.state} field="state" editData={editData} setEditData={setEditData} isEditing={editingSection === 'address'} />
                    <InfoRow label="Postal Code" value={company.postal_code} field="postal_code" editData={editData} setEditData={setEditData} isEditing={editingSection === 'address'} />
                    <InfoRow label="Country" value={company.country} field="country" editData={editData} setEditData={setEditData} isEditing={editingSection === 'address'} />
                </EditableSection>

                {/* Regional Settings */}
                <EditableSection
                    title="Regional Settings"
                    icon={FiGlobe}
                    section="regional"
                    editingSection={editingSection}
                    saving={saving}
                    onStartEdit={() => startEditing('regional', company)}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                >
                    <InfoRow
                        label="Timezone"
                        value={company.timezone}
                        field="timezone"
                        type="select"
                        options={[
                            { value: 'Australia/Melbourne', label: 'Australia/Melbourne' },
                            { value: 'Australia/Sydney', label: 'Australia/Sydney' },
                            { value: 'Australia/Brisbane', label: 'Australia/Brisbane' },
                            { value: 'Australia/Perth', label: 'Australia/Perth' },
                            { value: 'Pacific/Auckland', label: 'Pacific/Auckland' }
                        ]}
                        editData={editData}
                        setEditData={setEditData}
                        isEditing={editingSection === 'regional'}
                    />
                    <InfoRow
                        label="Currency"
                        value={company.currency}
                        field="currency"
                        type="select"
                        options={[
                            { value: 'AUD', label: 'AUD - Australian Dollar' },
                            { value: 'USD', label: 'USD - US Dollar' },
                            { value: 'EUR', label: 'EUR - Euro' },
                            { value: 'GBP', label: 'GBP - British Pound' },
                            { value: 'NZD', label: 'NZD - New Zealand Dollar' }
                        ]}
                        editData={editData}
                        setEditData={setEditData}
                        isEditing={editingSection === 'regional'}
                    />
                </EditableSection>

                {/* Subscription */}
                <EditableSection
                    title="Subscription & Billing"
                    icon={FiDollarSign}
                    section="subscription"
                    editingSection={editingSection}
                    saving={saving}
                    onStartEdit={() => startEditing('subscription', company)}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                >
                    <InfoRow
                        label="Subscription Plan"
                        value={company.subscription_plan}
                        field="subscription_plan"
                        type="select"
                        options={[
                            { value: 'free', label: 'Free' },
                            { value: 'starter', label: 'Starter' },
                            { value: 'professional', label: 'Professional' },
                            { value: 'enterprise', label: 'Enterprise' }
                        ]}
                        editData={editData}
                        setEditData={setEditData}
                        isEditing={editingSection === 'subscription'}
                    />
                    <InfoRow
                        label="Subscription Status"
                        value={company.subscription_status}
                        field="subscription_status"
                        type="select"
                        options={[
                            { value: 'trial', label: 'Trial' },
                            { value: 'active', label: 'Active' },
                            { value: 'suspended', label: 'Suspended' },
                            { value: 'cancelled', label: 'Cancelled' }
                        ]}
                        editData={editData}
                        setEditData={setEditData}
                        isEditing={editingSection === 'subscription'}
                    />
                    <InfoRow label="Base Price" value={`${company.currency} $${company.base_price}`} field="base_price" type="number" editData={editData} setEditData={setEditData} isEditing={editingSection === 'subscription'} />
                    <InfoRow label="Per User Price" value={`${company.currency} $${company.per_user_price}`} field="per_user_price" type="number" editData={editData} setEditData={setEditData} isEditing={editingSection === 'subscription'} />
                </EditableSection>

                {/* Limits */}
                <EditableSection
                    title="Limits & Quotas"
                    icon={FiTrendingUp}
                    section="limits"
                    editingSection={editingSection}
                    saving={saving}
                    onStartEdit={() => startEditing('limits', company)}
                    onCancel={cancelEditing}
                    onSave={saveEdit}
                >
                    <InfoRow label="Max Users" value={company.max_users || 'Unlimited'} field="max_users" type="number" editData={editData} setEditData={setEditData} isEditing={editingSection === 'limits'} />
                    <InfoRow label="Max Jobs" value={company.max_jobs || 'Unlimited'} field="max_jobs" type="number" editData={editData} setEditData={setEditData} isEditing={editingSection === 'limits'} />
                    <InfoRow label="Max Storage (GB)" value={company.max_storage_gb || 'Unlimited'} field="max_storage_gb" type="number" editData={editData} setEditData={setEditData} isEditing={editingSection === 'limits'} />
                </EditableSection>
            </div>

            {/* Users Section */}
            <div className="bg-white dark:bg-zinc-800 rounded-xl border border-gray-200 dark:border-zinc-700 p-6">
                <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                        <FiUsers className="w-5 h-5 text-blue-600" />
                        Users ({users.length})
                    </h3>
                    <button
                        onClick={() => setShowInviteModal(true)}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
                    >
                        <FiPlus className="w-4 h-4" />
                        Invite User
                    </button>
                </div>

                {/* Users List */}
                <div className="space-y-3">
                    {users.map((user) => (
                        <div key={user.id} className="flex items-center justify-between p-4 bg-gray-50 dark:bg-zinc-700/50 rounded-lg border border-gray-200 dark:border-zinc-600">
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center">
                                    <FiUsers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                                </div>
                                <div>
                                    <p className="font-medium text-gray-900 dark:text-white">{user.username || user.full_name}</p>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">{user.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg text-xs font-medium">
                                    {user.role}
                                </span>
                                <button
                                    onClick={() => navigate(`/${currentCompanySlug}/users/${user.id}`)}
                                    className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                    title="Edit user"
                                >
                                    <FiEdit2 className="w-4 h-4" />
                                </button>
                                <button
                                    onClick={() => handleDeleteUser(user.id, user.username || user.full_name)}
                                    className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    title="Delete user"
                                >
                                    <FiTrash2 className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    ))}
                    {users.length === 0 && (
                        <p className="text-center text-gray-500 dark:text-gray-400 py-8">No users yet</p>
                    )}
                </div>

                {/* Pending Invitations */}
                {pendingInvitations.length > 0 && (
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-zinc-700">
                        <h4 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
                            Pending Invitations ({pendingInvitations.length})
                        </h4>
                        <div className="space-y-3">
                            {pendingInvitations.map((invitation) => (
                                <div key={invitation.id} className="flex items-center justify-between p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
                                    <div className="flex items-center gap-3">
                                        <FiClock className="w-5 h-5 text-amber-600 dark:text-amber-400" />
                                        <div>
                                            <p className="font-medium text-gray-900 dark:text-white">{invitation.email}</p>
                                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                                Invited {formatDate(invitation.created_at)}
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <button
                                            onClick={() => handleResendInvitation(invitation.id)}
                                            className="p-2 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                                            title="Resend invitation"
                                        >
                                            <FiRefreshCw className="w-4 h-4" />
                                        </button>
                                        <button
                                            onClick={() => handleCancelInvitation(invitation.id)}
                                            className="p-2 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            title="Cancel invitation"
                                        >
                                            <FiX className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Invite User Modal */}
            <InviteUserModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                onSubmit={handleInviteUser}
                companyId={company.id}
            />
        </div>
    );
};

export default CompanyDetails;
